// Batch 0 (UX/IA overhaul) — THE signature enterprise-VMS element.
//
// Renders a camera's physical parentage as a breadcrumb — "Site › NVR (make
// model) › Ch 3" — so the Site → NVR → Channel hierarchy is visible on every
// screen that shows a camera, not just the Topology tab (the core gap). Drop
// it into camera lists, camera detail, alerts, health rows, recordings,
// detections, evidence.
//
// Degrades gracefully: any segment whose data is absent is simply omitted, so
// it's already useful with today's payload (which carries nvr_make/model +
// channel_no) and gets richer once Batch 1 adds site_name / source_id.
import * as React from "react";

import { buildDeviceContext } from "@/lib/deviceContext";
import type { CameraRef } from "@/types/deviceRef";
import { cn } from "@/lib/utils";

export interface DeviceContextLabelProps {
  camera: CameraRef;
  /** "full" (default) shows Site › NVR › Ch; "compact" shows NVR › Ch only. */
  variant?: "full" | "compact";
  className?: string;
}

export function DeviceContextLabel({
  camera,
  variant = "full",
  className,
}: DeviceContextLabelProps) {
  const segments = buildDeviceContext(camera, variant);
  const full = segments.join(" › ");
  if (segments.length === 0) {
    return <span className={cn("text-xs text-muted-foreground", className)}>—</span>;
  }
  // Single-line, truncates with an ellipsis when the row is narrow (e.g. a
  // long NVR name) rather than wrapping and breaking table layout; the full
  // "Site › NVR › Ch" path stays available on hover via the title.
  return (
    <span
      className={cn(
        "block max-w-full truncate align-bottom text-xs text-muted-foreground",
        className,
      )}
      title={full}
    >
      {segments.map((seg, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="px-1 opacity-50" aria-hidden>›</span>}
          <span>{seg}</span>
        </React.Fragment>
      ))}
    </span>
  );
}
